import { useState } from "react";
import { Progress, Text } from "@mantine/core";
import styles from "./styles/Player.module.css";
import { RangeSlider } from "./RangeSlider";
import { createTimeStructure } from "@/core/helpers";
import { usePlaybackProgress } from "@/hooks/usePlaybackProgress";

// The parts of the player that change every second. Each reads the playback time itself, so a tick re-renders
// these few elements and not the player around them (cover, links, buttons, menus).

/** The thin line under the slim player */
export const MiniProgress = () => {
    const { time, duration } = usePlaybackProgress();
    return (
        <Progress
            aria-label="Uppspelningens förlopp"
            className={styles.miniprogress}
            color="teal"
            size={3}
            value={duration > 0 ? Math.min(100, (time / duration) * 100) : 0}
        />
    );
};

/** The slider, which follows the hand while it is being moved */
export const PlayerTimeline = ({ onSeek }: { onSeek: (seconds: number) => void }) => {
    const { time: playedTime, duration } = usePlaybackProgress();
    // Where the listener has just moved to, until the player reports it: the slider follows the hand, not the lag
    const [moved, setMoved] = useState<{ to: number; from: number } | null>(null);
    if (moved && moved.from !== playedTime) {
        setMoved(null);
    }

    const change = (newValue: number | number[]) => {
        const value = Array.isArray(newValue) ? newValue[0] : newValue;
        setMoved({ to: value, from: playedTime });
        onSeek(value);
    };

    return <RangeSlider value={moved ? moved.to : playedTime} onChange={change} min={0} max={duration} />;
};

/** "00:12:03 / 00:40:24" */
export const PlayerClock = () => {
    const { time, duration } = usePlaybackProgress();
    return (
        <Text c="dimmed" size="sm">
            {createTimeStructure(time).formatted} / {createTimeStructure(duration).formatted}
        </Text>
    );
};
